import { LesionInfo } from '@models/Parameter';
import { AppColors, defaultColors } from './color';
import { SettingsData } from '../features/analysis/analysisModal/SettingsModal/SettingsModalSlice';
import { SETTING_CONTENTS_NAMES } from 'config/settingsConfig';
import { DicomNodeParams } from '@models/pacs';
import { volumeColorType } from './colorUtils';
import { AnalysisType, EditTarget, PresetType } from 'config/analysisConfig';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import { CT_ANALYSIS_TYPE_NAMES } from 'config/labels';
import { volumeKeys } from './render3DConfig';



// Developer switch for server-state-based feature visibility.
// false: follow getServerState() and hide unsupported server features.
// true : ignore getServerState() and show every feature/menu for development.
export const FORCE_ENABLE_ALL_SERVER_FEATURES_FOR_DEV = false;

export const DEFAULT_LESION_STACK_SPACING_MM = 2;
export const DEFAULT_LESION_STACK_THICKNESS_MM = 1;
export const LESION_STACK_SPACING_MIN_MM = 0.1;
export const LESION_STACK_SPACING_MAX_MM = 10;
export const LESION_STACK_THICKNESS_MIN_MM = 0.5;
export const LESION_STACK_THICKNESS_MAX_MM = 10;

export function normalizeLesionStackSpacingMm(value: unknown): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_LESION_STACK_SPACING_MM;
  const clamped = Math.min(
    LESION_STACK_SPACING_MAX_MM,
    Math.max(LESION_STACK_SPACING_MIN_MM, numeric),
  );
  return Math.round(clamped * 10) / 10;
}

export function normalizeLesionStackThicknessMm(value: unknown): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_LESION_STACK_THICKNESS_MM;
  const clamped = Math.min(
    LESION_STACK_THICKNESS_MAX_MM,
    Math.max(LESION_STACK_THICKNESS_MIN_MM, numeric),
  );
  return Math.round(clamped * 10) / 10;
}

export type AutoCaptureTemplate =
  | 'segmentation3d'
  | 'chamberSingle'
  | 'chamberFull'
  | 'plaqueSingleCPR'
  | 'plaqueMainCPR'
  | 'plaqueFullCPR';

export interface CustomAutoCaptureTool {
  /** id suffix. 실제 ToolId는 AutoCapture_Custom_${id} 형태로 생성됨 */
  id: string;
  name: string;
  analyses: AnalysisType[];
  template: AutoCaptureTemplate;
  options?: Record<string, any>;
}

export type CPRGraphType = 'diameter' | 'area' | 'none';
export type CPRSeriesMode = 'combined' | 'splitCpr' | 'splitAll';
export type CprModificationTool = 'none' | 'line' | 'sculpt' | 'brush';
export type CprPixelBrushMode = 'draw' | 'erase';

export type LightingOption = {
  enabled: boolean;
  ambient: number;
};
export type LightingOptions = {
  VR: LightingOption;
  Surface3D: LightingOption;
};

export interface Lesion {
  name: string;
  value: [number, number, number];
}

export interface PlaqueBranch {
  vesselIndex: number;
  vesselName: string;
  lesions: Lesion[];
}

export type PlaqueStatus = PlaqueBranch[];

export type AnalysisParams = {
  defaultWindowWidth: number;
  defaultWindowLevel: number;
  isInverted?: boolean;
  useDefaultWindow: boolean;
  enabled: boolean;
};

export const DEFAULT_PARAMS: AnalysisParams = {
  defaultWindowWidth: 600,
  defaultWindowLevel: 200,
  isInverted: false,
  useDefaultWindow: false,
  enabled: true,
};


export interface CustomWindowLevelPreset {
  presets: PresetType[];
}

export interface ISRDataProp {
  name: string;
  length: number;
  isr: string;
  index: [number, number];
  branchid: number;
}

export interface TaviPlaneConfig {
  center: [number, number, number];
  normal: [number, number, number];
  dx: [number, number, number];
  dy: [number, number, number];
}

export interface TaviPlaneOverrideState {
  annulus: TaviPlaneConfig | null;
  sinus: TaviPlaneConfig | null;
  sinotubularjunction: TaviPlaneConfig | null;
}

type ChamberCapture<T> = {
  Axial: T;
  FourChamber: T;
  TwoChamber: T;
  ShortAxis: T;
};

export type ChamberViewGuidePan = {
  guideDelta: [number, number, number];
  directionBackgroundMovement: [number, number, number];
};
// 공용 변수
export interface CommonConfig {
  upscale: number;
  autoLogin: boolean;

  coronary_front_expand: number;
  coronary_back_expand: number;

  generalSetting: Partial<SettingsData['general']>;
  label: Label;
  viewOverlayLabels: ViewOverlayLabels;
  annotationState: {
    Axis: boolean;
    Measurement: boolean;
    Annotations: boolean;
    PatientInfo: boolean;
  };

  lightingOptions: {
    VR:LightingOption;
    Surface3D:LightingOption;
  };

  lesionStackSpacingMm: number;
  lesionStackThicknessMm: number;

  // 서버 저장
  defaultServer: DicomNodeParams[];
  customWindowLevelPreset: CustomWindowLevelPreset;
  customAutoCaptureTools: CustomAutoCaptureTool[];

  captureWidth: number;
  captureHeight: number;
  captureCPRWidth: number;
  uploadWidth: number;
  uploadHeight: number;

  stretchedAngle: number;
  stretchedCount: number;
  straightenedAngle: number;
  straightenedCount: number;

  modelCaptureAngleH: number;
  modelCaptureAngleV: number;
  modelCaptureCountH: number;
  modelCaptureCountV: number;

  mainBranchOrder: string[];
  chamberCaptureOrder: string[];

  chamberCaptureDefaultThickness: number;
  chamberCaptureDefaultSpacing: number;

  sendmode: string;
  vrType: volumeColorType;
  renderingModeMIP: 'AortaCoronary' | 'AortaCoronaryWall' | 'All' | 'Ventricle';
  volumeDilation: { target: volumeKeys[] | undefined; enabled: boolean };
  modelCaptureFit: boolean;
  cprGroupByMainBranch: boolean;
  cprGraphType: CPRGraphType;
  cprSeriesMode: CPRSeriesMode;
  cprSplitIncludeModel: boolean;
  cprSplitIncludeModelStretched: boolean;
  cprSplitIncludeModelStraightened: boolean;
  cprSeriesOrder: string[];

  modelCaptureVR: {
    ventricle: {
      capture: boolean;
      description: string;
    };
    ventricleHeartCut: {
      capture: boolean;
      description: string;
    };
    coronaryAorta: {
      capture: boolean;
      description: string;
    };
    MIP: {
      capture: boolean;
      description: string;
    };
    current: {
      capture: boolean;
      description: string;
    };
    la: {
      capture: boolean;
      description: string;
    };
  };
  analysisParams: Record<AnalysisType, AnalysisParams>;
  cprSeriesSeparation: boolean;
  cprSculptSize: number;
  cprPixelBrushSize: number;
  cprPixelBrushMode: CprPixelBrushMode;
  regulatorConnectThickness: number;
  regulatorConnectHuEnabled: boolean;
  regulatorConnectHuMin: number;
  regulatorConnectHuMax: number;

  chamberCaptureDefaultThicknessMethod: 'Average' | 'MIP' | 'MinIP';
  chamberCaptureDefaultImageSize: number;
  chamberCaptureInterpolation: 'nearest' | 'linear';
  // chamberCaptureDefaultWindowLevel: number;
  // chamberCaptureDefaultWindowWidth: number;
  chamberCaptureWindowLevel: number;
  chamberCaptureWindowWidth: number;
  chamberCaptureImageSize: ChamberCapture<number>;
  chamberCaptureThickness: ChamberCapture<number>;
  chamberCaptureThicknessMethod: ChamberCapture<'Average' | 'MIP' | 'MinIP'>;
  chamberCaptureSpacing: ChamberCapture<number>;
  chamberCaptureGuideRollAngle: ChamberCapture<number>;
  chamberCaptureGuideMovement: ChamberCapture<[number, number, number]>;
  chamberCaptureDirectionBackgroundMovement: ChamberCapture<
    [number, number, number]
  >;
  chamberCaptureRollAngle: ChamberCapture<number>;
  chamberCaptureAdditionalSlice: ChamberCapture<number>;
  chamberCaptureSliceHalfNumber: ChamberCapture<number>;
  chamberCaptureFlipVertical: ChamberCapture<boolean>;
  chamberCaptureFlipHorizontal: ChamberCapture<boolean>;
  chamberCaptureFlipDirection: ChamberCapture<boolean>;
  // ~ 서버 저장
}

// 시리즈 별 변수
export interface SeriesConfig {
  // aorta avc
  aorta_basewidth: number;
  aorta_id: number;
  tavi_aorta_id: number;
  aorta_start_index: number;
  aorta_current_index: number;
  tavi_start_index: number;
  tavi_current_index: number;
  avcCalciumScore: number;
  avcCalciumVolume: number;
  agatstonScore: number[];

  // coronary
  isr_data: Array<Map<number, ISRDataProp>>;
  stenosisResult: string;
  stenosisLesionInfo: LesionInfo[][];
  motherBranch: number[];

  // myocardial
  territory_branchid: number;
  territory_input_idx: number;
  total: {
    volume: number;
    lvMass: number;
    vmRatio: number;
  };
  dist: {
    rca: { mass: number; pct: number };
    lad: { mass: number; pct: number };
    lcx: { mass: number; pct: number };
  };
  risk: {
    volume: number;
    lvMass: { mass: number; pct: number };
    vmRatio: number;
  };

  // Segment option
  heartCut: boolean;
  ventricle: boolean;
  coronaryAorta: boolean;
  visibleMIP: boolean;
  laCapture: boolean;
  laText: boolean;

  //Plaque
  plaqueStatus: PlaqueStatus;

  // EF
  reportEF: {
    minChamberLV: string;
    maxChamberLV: string;
    percents: { id: string; value: number }[];
    LV: {
      ejectionFraction: string;
      endDiastolicVolume: string;
      endSystolicVolume: string;
      strokeVolume: string;
      endDiastolicMyocardialVolume: string;
      endDiastolicMyocardialMass: string;
      chamber: { id: string; value: number }[];
    };
    RV: {
      ejectionFraction: string;
      endDiastolicVolume: string;
      endSystolicVolume: string;
      strokeVolume: string;
      endDiastolicMyocardialVolume: string;
      endDiastolicMyocardialMass: string;
      chamber: { id: string; value: number }[];
    };
  };

  // TAVI
  taviPlaneOverrides: TaviPlaneOverrideState;

  //FFR
  shouldRunFFR :boolean;

  chamberViewGuidePan: ChamberCapture<ChamberViewGuidePan>;
  chamberViewScoutDisplayRoll: ChamberCapture<number>;
}

export const labelKeys = [
  'lumen',
  'fai',
  'plaque',
  'centerline',
  'lumenoutline',
  'plaqueoutline',
  'markerline',
  'guideline',
  'stenosisline',
  'isrline',
  'aorta',
  'avc',
  'lesionline',
  'axial',
] as const;
export type LabelKey = (typeof labelKeys)[number];

export type Label = Record<LabelKey, boolean>;

export type Surface3DOverlayLabel = {
  lesion: boolean;
  centerline: boolean;
  label: boolean;
  stenosisLesion: boolean;
};

export type ViewOverlayLabels = {
  cpr: Label;
  crossSection: Label;
  surface3D: Surface3DOverlayLabel;
};

export function LabelAllOnOff(label: Label | undefined, value: boolean) {
  if (!label) return;
  for (const k of labelKeys) label[k] = value;
}
export const DEFAULT_LABEL: Label = Object.fromEntries(
  labelKeys.map((k) => [k, true]),
) as Label;

export const DEFAULT_VIEW_OVERLAY_LABELS: ViewOverlayLabels = {
  cpr: {
    ...DEFAULT_LABEL,
    lumenoutline: false,
  },
  crossSection: { ...DEFAULT_LABEL },
  surface3D: {
    lesion: true,
    centerline: true,
    label: true,
    stenosisLesion: true,
  },
};

export const ANALYSIS_TYPES = Object.keys(
  CT_ANALYSIS_TYPE_NAMES,
) as AnalysisType[];

// 저장하지 않는 일시적 변수들
export interface EtcConfig {
  basewidth: number;
  colors: AppColors;
  aorta_colors: Array<[number, number, number, number]>;
  aorta_opacitys: Array<[number, number]>;
  aorta_indexs: number[];
  avc_indexs: number[];
  branchArea: Map<number, [string, number][]>;
  oriHuData: vtkImageData | undefined;
  isCapture: boolean;
  isSvgCapture: boolean;
  isLowSegReady: boolean;
  rrPercent: number;
  rrPercent_ef: number;
  ffr_selected_centerline_id: number | null;
  EditMode: EditTarget;
  cprModificationTool: CprModificationTool;

  //세그멘퉤이션 상태
  analysisStatus: {
    default: string;
    lawt: string;
    segmentation: string;
    aorta: string;
    aortacenterline: string;
    coronary: string;
    onnxruntime: string;
  };

  segmentationLabel: Map<
    string,
    {
      opacity: number;
      visibility: boolean;
    }
  >;

  manualColors: Record<'2D' | '3D', [number, number, number]>;
  manualLabelmapStyle: 'fill' | 'outline';
  manual3DTool: {
    eraser: boolean;
    initialPoint: boolean;
  };
  manual3DApply: boolean;
  viewportSwitchPendingMap: {
    Axial: boolean;
    Coronal: boolean;
    Sagittal: boolean;
    Surface3D:boolean;
  };

  modifyRun:boolean;
}

export type AppConfig = CommonConfig & SeriesConfig & EtcConfig;

const GLOBAL_COMMON_KEYS = [
    'upscale',
    'autoLogin',
    'coronary_front_expand',
    'coronary_back_expand',
    'defaultServer',
    'captureWidth',
    'captureHeight',
    'captureCPRWidth',
    'uploadWidth',
    'uploadHeight',
    'stretchedAngle',
    'stretchedCount',
    'straightenedAngle',
    'straightenedCount',
    'chamberCaptureOrder',
    'chamberCaptureDefaultThickness',
    'chamberCaptureDefaultThicknessMethod',
    'chamberCaptureDefaultSpacing',
    'chamberCaptureDefaultImageSize',
    'chamberCaptureInterpolation',
    'cprGroupByMainBranch',
    'cprGraphType',
    'cprSeriesMode',
    'cprSplitIncludeModel',
    'cprSplitIncludeModelStretched',
    'cprSplitIncludeModelStraightened',
    'cprSeriesOrder',
    // 'chamberCaptureDefaultWindowLevel',
    // 'chamberCaptureDefaultWindowWidth',
    'chamberCaptureWindowLevel',
    'chamberCaptureWindowWidth',
    'chamberCaptureImageSize',
    'chamberCaptureThickness',
    'chamberCaptureThicknessMethod',
    'chamberCaptureSpacing',
    'chamberCaptureGuideRollAngle',
    'chamberCaptureGuideMovement',
    'chamberCaptureDirectionBackgroundMovement',
    'chamberCaptureRollAngle',
    'chamberCaptureAdditionalSlice',
    'chamberCaptureSliceHalfNumber',
    'chamberCaptureFlipVertical',
    'chamberCaptureFlipHorizontal',
    'chamberCaptureFlipDirection',
    'vrType',
    'renderingModeMIP',
    'volumeDilation',
    'generalSetting',
    'customWindowLevelPreset',
    'modelCaptureAngleH',
    'modelCaptureAngleV',
    'modelCaptureCountH',
    'modelCaptureCountV',
    'mainBranchOrder',
    'label',
    'viewOverlayLabels',
    'sendmode',
    'annotationState',
    'modelCaptureVR',
    'modelCaptureFit',
    'analysisParams',
    'customAutoCaptureTools',
    'cprSeriesSeparation',
    'cprSculptSize',
    'cprPixelBrushSize',
    'cprPixelBrushMode',
    'regulatorConnectThickness',
    'regulatorConnectHuEnabled',
    'regulatorConnectHuMin',
    'regulatorConnectHuMax',
    'lightingOptions',
    'lesionStackSpacingMm',
    'lesionStackThicknessMm',
  ] as const;

// Global(common) keys excluded from server sync.
// Add keys here when they must stay local-only.
const SERVER_EXCLUDED_COMMON_KEYS = [
  'upscale',
  'autoLogin',
  'coronary_front_expand',
  'coronary_back_expand',
  'defaultServer',
  'generalSetting',
  'label',
  'viewOverlayLabels',
  'annotationState',
  'modelCaptureVR',
  'cprSculptSize',
  'cprPixelBrushSize',
  'cprPixelBrushMode',
  'regulatorConnectThickness',
  'regulatorConnectHuEnabled',
  'regulatorConnectHuMin',
  'regulatorConnectHuMax',
  'lightingOptions',
  'lesionStackSpacingMm',
  'lesionStackThicknessMm',
  'volumeDilation',
] as const;

const SERVER_COMMON_KEYS = GLOBAL_COMMON_KEYS.filter(
  (k) => !SERVER_EXCLUDED_COMMON_KEYS.includes(k as any),
) as readonly (typeof GLOBAL_COMMON_KEYS)[number][];

export const PARTITION_KEYS = {
  global: GLOBAL_COMMON_KEYS,
  serverExcluded: SERVER_EXCLUDED_COMMON_KEYS,
  server: SERVER_COMMON_KEYS,

  series: [
    'aorta_basewidth',
    'aorta_id',
    'tavi_aorta_id',
    'aorta_start_index',
    'aorta_current_index',
    'tavi_start_index',
    'tavi_current_index',
    'avcCalciumScore',
    'avcCalciumVolume',
    'agatstonScore',
    'isr_data',
    'stenosisResult',
    'stenosisLesionInfo',
    'motherBranch',
    'territory_branchid',
    'territory_input_idx',
    'total',
    'dist',
    'risk',
    'heartCut',
    'reportEF',
    'plaqueStatus',
    'visibleMIP',
    'laCapture',
    'taviPlaneOverrides',
    'shouldRunFFR',
    'chamberViewGuidePan',
    'chamberViewScoutDisplayRoll',
  ] as const,

  volatile: [
    'basewidth',
    'colors',
    'aorta_colors',
    'aorta_opacitys',
    'aorta_indexs',
    'avc_indexs',
    'branchArea',
    'label',
    'laText',
  ] as const,
};

export type GlobalField = (typeof PARTITION_KEYS.global)[number];
export type ServerExcludedField = (typeof PARTITION_KEYS.serverExcluded)[number];
export type ServerCommonField = Exclude<GlobalField, ServerExcludedField>;
export type SeriesField = (typeof PARTITION_KEYS.series)[number];
export type VolatileField = (typeof PARTITION_KEYS.volatile)[number];
export type ServerCommonConfig = Pick<CommonConfig, ServerCommonField>;

export const MAP_FIELDS_IN_SERIES: SeriesField[] = ['isr_data'];

const initChamberCapture = <T>(value: T): ChamberCapture<T> => {
  return {
    Axial: value,
    FourChamber: value,
    TwoChamber: value,
    ShortAxis: value,
  };
};

const createZeroChamberViewGuidePan = (): ChamberViewGuidePan => ({
  guideDelta: [0, 0, 0],
  directionBackgroundMovement: [0, 0, 0],
});

const initChamberViewGuidePan = (): ChamberCapture<ChamberViewGuidePan> => ({
  Axial: createZeroChamberViewGuidePan(),
  FourChamber: createZeroChamberViewGuidePan(),
  TwoChamber: createZeroChamberViewGuidePan(),
  ShortAxis: createZeroChamberViewGuidePan(),
});

export const defaultConfig: AppConfig = {
  autoLogin: false,

  // 공용
  upscale: 3,
  coronary_front_expand: 50,
  coronary_back_expand: 50,
  defaultServer: [],
  captureWidth: 1024,
  captureHeight: 1024,
  captureCPRWidth: 300,
  uploadWidth: 1024,
  uploadHeight: 1024,
  customWindowLevelPreset: {
    presets: [],
  },
  label: { ...DEFAULT_LABEL },
  viewOverlayLabels: {
    cpr: { ...DEFAULT_VIEW_OVERLAY_LABELS.cpr },
    crossSection: { ...DEFAULT_VIEW_OVERLAY_LABELS.crossSection },
    surface3D: { ...DEFAULT_VIEW_OVERLAY_LABELS.surface3D },
  },
  sendmode: '1',

  stretchedAngle: 30,
  stretchedCount: 12,
  straightenedAngle: 30,
  straightenedCount: 12,
  chamberCaptureOrder: [
    'axialCapture',
    '2chamberCapture',
    '4chamberCapture',
    'shortaxisCapture',
  ],
  vrType: 'warm',
  renderingModeMIP: 'All',
  volumeDilation: { target: undefined, enabled: false },
  modelCaptureAngleH: 30,
  modelCaptureAngleV: 30,
  modelCaptureCountH: 12,
  modelCaptureCountV: 12,
  mainBranchOrder: ['RCA', 'LAD', 'LCX'],
  chamberCaptureDefaultThickness: 3,
  chamberCaptureDefaultThicknessMethod: 'MIP',
  chamberCaptureDefaultSpacing: 3,
  chamberCaptureDefaultImageSize: 180,
  chamberCaptureInterpolation: 'linear',
  cprGroupByMainBranch: true,
  cprGraphType: 'none',
  cprSeriesMode: 'combined',
  cprSplitIncludeModel: true,
  cprSplitIncludeModelStretched: true,
  cprSplitIncludeModelStraightened: true,
  cprSeriesOrder: ['model', 'stretched', 'straightened'],
  // chamberCaptureDefaultWindowLevel: 80,
  // chamberCaptureDefaultWindowWidth: 600,
  chamberCaptureWindowLevel: 0,
  chamberCaptureWindowWidth: 0,
  chamberCaptureImageSize: initChamberCapture(0),
  chamberCaptureThickness: initChamberCapture(0),
  chamberCaptureThicknessMethod: initChamberCapture('MIP'),
  chamberCaptureSpacing: initChamberCapture(0),
  chamberCaptureGuideRollAngle: initChamberCapture(0),
  chamberCaptureGuideMovement: initChamberCapture([0, 0, 0] as [
    number,
    number,
    number,
  ]),
  chamberCaptureDirectionBackgroundMovement: initChamberCapture([
    0, 0, 0,
  ] as [number, number, number]),
  chamberViewGuidePan: initChamberViewGuidePan(),
  chamberViewScoutDisplayRoll: initChamberCapture(0),
  chamberCaptureRollAngle: initChamberCapture(0),
  chamberCaptureAdditionalSlice: initChamberCapture(0),
  chamberCaptureSliceHalfNumber: initChamberCapture(30),
  chamberCaptureFlipVertical: initChamberCapture(false),
  chamberCaptureFlipHorizontal: initChamberCapture(false),
  chamberCaptureFlipDirection: initChamberCapture(false),
  generalSetting: {
    theme: SETTING_CONTENTS_NAMES.general.themes.darkMode,
    font: SETTING_CONTENTS_NAMES.general.fonts.pretendard,
    cacheLimit: 1024,
    automaticallyClearCache: true,
    intervalDay: 1,
    usageStorage: 120,
    freeStorage: 80,
    storageAlertNotification: true,
    GPUAcceleration: true,
    renderingQuality: 'high',
    enableProxyServer: false,
    proxyIP: '',
    proxyPort: '',
  },
  annotationState: {
    Axis: true,
    Measurement: true,
    Annotations: true,
    PatientInfo: true,
  },
  modelCaptureVR: {
    current: {
      capture: true,
      description: '3D Multi-view',
    },
    MIP: {
      capture: false,
      description: 'MIP',
    },
    ventricle: {
      capture: false,
      description: 'Ventricle',
    },
    ventricleHeartCut: {
      capture: false,
      description: 'HeartCut',
    },
    coronaryAorta: {
      capture: false,
      description: 'Coronary-Aorta',
    },
    la: {
      capture: false,
      description: 'Left Atrial Volume',
    },
  },
  modelCaptureFit: true,
  customAutoCaptureTools: [],
  analysisParams: Object.fromEntries(
    ANALYSIS_TYPES.map((t) => [t, { ...DEFAULT_PARAMS }]),
  ) as Record<AnalysisType, AnalysisParams>,
  cprSeriesSeparation: false,
  lightingOptions: {
    VR: { enabled: true, ambient: 0.2 },
    Surface3D: { enabled: true, ambient: 0.2 },
  },
  lesionStackSpacingMm: DEFAULT_LESION_STACK_SPACING_MM,
  lesionStackThicknessMm: DEFAULT_LESION_STACK_THICKNESS_MM,

  // 시리즈
  aorta_basewidth: 300,
  aorta_id: 49,
  tavi_aorta_id: 48,
  aorta_start_index: 0,
  aorta_current_index: 0,
  tavi_start_index: 0,
  tavi_current_index: 0,
  avcCalciumScore: 0,
  avcCalciumVolume: 0,
  agatstonScore: Array(10).fill(0),
  isr_data: Array.from({ length: 3 }, () => new Map<number, ISRDataProp>()),
  stenosisResult: '',
  stenosisLesionInfo: [],
  motherBranch: [],
  territory_branchid: 0,
  territory_input_idx: 0,
  total: {
    volume: 0,
    lvMass: 0,
    vmRatio: 0,
  },
  dist: {
    rca: { mass: 0, pct: 0 },
    lad: { mass: 0, pct: 0 },
    lcx: { mass: 0, pct: 0 },
  },
  risk: {
    volume: 0,
    lvMass: { mass: 0, pct: 0 },
    vmRatio: 0,
  },

  heartCut: false, // 얘만 시리즈마다 개별동작
  ventricle: true,
  coronaryAorta: false,
  visibleMIP: false,
  laCapture: false,
  laText: false,

  reportEF: {
    minChamberLV: '',
    maxChamberLV: '',
    percents: [],
    LV: {
      ejectionFraction: '',
      endDiastolicVolume: '',
      endSystolicVolume: '',
      strokeVolume: '',
      endDiastolicMyocardialVolume: '',
      endDiastolicMyocardialMass: '',
      chamber: [],
    },
    RV: {
      ejectionFraction: '',
      endDiastolicVolume: '',
      endSystolicVolume: '',
      strokeVolume: '',
      endDiastolicMyocardialVolume: '',
      endDiastolicMyocardialMass: '',
      chamber: [],
    },
  },
  taviPlaneOverrides: {
    annulus: null,
    sinus: null,
    sinotubularjunction: null,
  },
  plaqueStatus: [],
  shouldRunFFR :false,

  // 기타
  basewidth: 128,
  colors: defaultColors,
  aorta_colors: Array.from({ length: 256 }, (_, i) => [i, 0, 0, 0]),
  aorta_opacitys: Array.from({ length: 256 }, (_, i) => [i, 0]),
  aorta_indexs: [],
  avc_indexs: [],
  branchArea: new Map(),
  oriHuData: undefined,
  isCapture: false,
  isSvgCapture:false,
  isLowSegReady: false,
  ffr_selected_centerline_id: null,
  EditMode: null,
  cprModificationTool: 'none',
  cprSculptSize: 5,
  cprPixelBrushSize: 5,
  cprPixelBrushMode: 'draw',
  regulatorConnectThickness: 3,
  regulatorConnectHuEnabled: false,
  regulatorConnectHuMin: -1024,
  regulatorConnectHuMax: 3071,

  // [mysong]
  // 저장 안 하는 변수
  rrPercent: 0,
  rrPercent_ef: 0,
  analysisStatus: {
    default: 'pending',
    lawt: 'pending',
    segmentation: 'pending',
    aorta: 'pending',
    aortacenterline: 'pending',
    coronary: 'pending',
    onnxruntime: 'pending',
  },
  segmentationLabel: new Map(),
  manualColors: {
    '2D': [216, 23, 87],
    '3D': [255, 255, 255],
  },
  manualLabelmapStyle: 'fill',
  manual3DTool: {
    eraser: false,
    initialPoint: false,
  },
  manual3DApply: false,
  viewportSwitchPendingMap: {
    Surface3D: true,
    Axial: true,
    Coronal: true,
    Sagittal: true,
  },
  modifyRun:false,
};
